import React from 'react';
import {
  string,
  bool,
  arrayOf,
  shape
} from 'prop-types';
import { Dropdown } from 'semantic-ui-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import l10n from '../../../l10n/en';
import './dropdown.less';

dayjs.extend(utc);

export const maxEncodedDescriptionLength = 1700;

export const normalizeDescription = (description, url) =>
{
  const descriptionWithUrl = `${url}\n\n${description}`;

  if (encodeURIComponent(descriptionWithUrl).length > maxEncodedDescriptionLength)
  {
    return url;
  }

  return descriptionWithUrl;
};

export const normalizeEvent = ({
  id,
  title,
  description,
  start,
  end,
  location,
  url,
  allDay,
  sessions
}) => ({
  id,
  title,
  start,
  end,
  description: normalizeDescription(description, url),
  location,
  url,
  allDay,
  // eslint-disable-next-line object-curly-newline
  sessions: sessions && sessions.length ? sessions : [{ title, start, end, allDay }]
});

export const normalizeDateString = dateString => (dateString || '').replace(/[^\d\w]/g, '');

export const normalizeIcsString = icsString => (icsString || '')
  .replace(/\n/gm, '\\n')
  .replace(/(\\n)[\s\t]+/gm, '\\n');

export const stringifyUriParams = params => Object.entries(params)
  .map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  )
  .join('&');

export const office365 = ({
  title: subject,
  description: body,
  start: startdt,
  end: enddt,
  location
}) => `https://outlook.office365.com/owa/?path=/calendar/action/compose&rru=addevent&${stringifyUriParams({
  subject,
  startdt,
  enddt,
  body,
  location
})}`;

export const yahoo = ({
  title,
  description: desc,
  start,
  end,
  // eslint-disable-next-line camelcase
  location: in_loc
}) => `https://calendar.yahoo.com/?v=60&${stringifyUriParams({
  title,
  st: normalizeDateString(start),
  et: normalizeDateString(end),
  desc,
  in_loc
})}`;

export const google = ({
  title: text,
  description: details,
  start,
  end,
  location
}) => `https://calendar.google.com/calendar/render?action=TEMPLATE&${stringifyUriParams({
  text,
  details,
  location,
  dates: `${normalizeDateString(start)}/${normalizeDateString(end)}`
})}`;

// eslint-disable-next-line no-empty-pattern
export const ics = ({
  id,
  description,
  url,
  location,
  sessions
}) =>
{
  const normalizedDescription = normalizeIcsString(description);
  const normalizedLocation = normalizeIcsString(location);
  const stamp = normalizeDateString(new Date().toISOString().replace(/\.[\d]+Z/i, 'Z'));
  const calendarChunks = [
    { key: 'BEGIN', value: 'VCALENDAR' },
    { key: 'VERSION', value: '2.0' },
    { key: 'METHOD', value: 'PUBLISH' },
    ...sessions.reduce(
      (acc, { title, start, end }, i) => [
        ...acc,
        { key: 'BEGIN', value: 'VEVENT' },
        { key: 'UID', value: `WILD_APRICOT_EVENT_${id}_SESSION_${i}` },
        { key: 'URL', value: url },
        { key: 'DTSTART', value: normalizeDateString(start) },
        { key: 'DTEND', value: normalizeDateString(end) },
        { key: 'SUMMARY', value: normalizeIcsString(title) },
        { key: 'DESCRIPTION', value: normalizedDescription },
        { key: 'LOCATION', value: normalizedLocation },
        { key: 'DTSTAMP', value: stamp },
        { key: 'END', value: 'VEVENT' }
      ],
      []
    ),
    { key: 'END', value: 'VCALENDAR' }
  ];

  return calendarChunks.reduce(
    (calendarUrl, { key, value }) => `${calendarUrl}${value ? `${key}:${encodeURIComponent(`${value}\r\n`)}` : ''}`,
    'data:text/calendar;charset=utf8,'
  );
};

const AddToCalendarDropdown = ({
  id,
  title,
  description,
  start,
  end,
  location,
  url,
  allDay,
  sessions
}) =>
{
  const today = dayjs(new Date()).format('YYYY-MM-DD');
  const event = normalizeEvent({
    id,
    title,
    description,
    start,
    end,
    location,
    url,
    allDay,
    sessions
  });

  const target = '_blank';
  const rel = 'noopener noreferrer nofollow';
  const icsHref = ics(event);
  const icsFilename = `${today}-Invite-to-event-${id}.ics`;
  const links = (event.sessions && event.sessions.length > 1)
    ? {
      apple: {
        href: icsHref,
        label: l10n.APPLE_LINK_LABEL,
        download: icsFilename
      },

      outlook: {
        href: icsHref,
        label: l10n.OUTLOOK_LINK_LABEL,
        download: icsFilename
      },

      ics: {
        href: icsHref,
        label: l10n.ICS_LINK_LABEL,
        download: icsFilename
      }
    }
    : {
      apple: {
        href: icsHref,
        label: l10n.APPLE_LINK_LABEL,
        download: icsFilename
      },

      google: {
        href: google(event),
        label: l10n.GOOGLE_LINK_LABEL,
        target,
        rel
      },

      office365: {
        href: office365(event),
        label: l10n.OFFICE365_LINK_LABEL,
        target,
        rel
      },

      outlook: {
        href: icsHref,
        label: l10n.OUTLOOK_LINK_LABEL,
        download: icsFilename
      },

      ics: {
        href: icsHref,
        label: l10n.ICS_LINK_LABEL,
        download: icsFilename
      }
    };

  return (
    <Dropdown
      inline
      icon="calendar plus outline"
      text={l10n.DROPDOWN_LINK_LABEL}
      className="-wa-add-event-to-calendar-dropdown"
    >
      <Dropdown.Menu direction="right">
        {Object.entries(links).map(
          ([key, { label, ...props }]) => (
            <Dropdown.Item as="a" {...props} content={label} key={key} />
          )
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

AddToCalendarDropdown.propTypes = {
  id: string.isRequired,
  title: string,
  description: string,
  start: string.isRequired,
  end: string,
  location: string,
  url: string,
  allDay: bool,
  sessions: arrayOf(
    shape({
      title: string.isRequired,
      start: string.isRequired,
      end: string,
      allDay: bool
    })
  )
};

AddToCalendarDropdown.defaultProps = {
  title: '',
  description: '',
  end: null,
  location: '',
  url: '',
  allDay: false,
  sessions: []
};

export default AddToCalendarDropdown;
